import { AppIcon } from '@backrs_ui/common-ui';

import {
  CareerQuestion,
  CareerUserResponseAnswer,
  CareerUserResponseSet,
} from '@backrs_ui/grow-ui/src/models/models';
import { isOptionDisabledDueToMutualExclusivity } from '@backrs_ui/grow-ui/src/pages/Survey/survey.util';

import { Box, SxProps, Theme } from '@mui/material';

const MAX_SOURCES_OF_MEANING = 4;

function getMaxSelections(question: CareerQuestion): number {
  if (question.calculator_key === 'sources_of_meaning') {
    return MAX_SOURCES_OF_MEANING;
  }

  // All other multi-selects: can't select every option
  return question.options.length - 1;
}

type TradeoffAnswerPillsProps = Readonly<{
  question: CareerQuestion;
  response: CareerUserResponseAnswer | undefined;
  onSelect: (questionId: number, optionIds: number[]) => void;
  draftResponseSet: CareerUserResponseSet;
  careerQuestions: CareerQuestion[];
}>;

export function TradeoffAnswerPills({
  question,
  response,
  onSelect,
  draftResponseSet,
  careerQuestions,
}: TradeoffAnswerPillsProps) {
  const selectedOptionIds = response?.career_question_option_ids ?? [];
  const maxSelections = question.is_multi_select ? getMaxSelections(question) : 1;
  const atMax = !!question.is_multi_select && selectedOptionIds.length >= maxSelections;
  const isMulti = !!question.is_multi_select;

  const handleSelect = (optionId: number) => {
    if (question.is_multi_select) {
      const isSelected = selectedOptionIds.includes(optionId);
      const updated = isSelected
        ? selectedOptionIds.filter((id) => id !== optionId)
        : [...selectedOptionIds, optionId];

      onSelect(question.id, updated);
    } else {
      onSelect(question.id, [optionId]);
    }
  };

  return (
    <Box sx={containerSx}>
      {question.options
        .slice()
        .sort((a, b) => a.sequence - b.sequence)
        .map((option) => {
          const isSelected = selectedOptionIds.includes(option.id);
          const label = option.aspirational_title || option.answer;

          // Mutual exclusivity: disable if the same option is selected in the paired question
          const isMutuallyExcluded = isOptionDisabledDueToMutualExclusivity(
            option.answer,
            question.calculator_key,
            draftResponseSet,
            careerQuestions,
          );

          // Max selections: disable unselected options when at the limit
          const isDisabled = !!isMutuallyExcluded || (atMax && !isSelected);

          return (
            <Box
              key={option.id}
              id={`tradeoff-option-${option.id}`}
              onClick={() => !isDisabled && handleSelect(option.id)}
              sx={answerSx(isSelected, isDisabled)}
              role="button"
              tabIndex={isDisabled ? -1 : 0}
              aria-disabled={isDisabled}
              onKeyDown={(e) => {
                if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  handleSelect(option.id);
                }
              }}
            >
              <Box sx={indicatorSx(isSelected, isMulti)}>
                {isSelected &&
                  (isMulti ? (
                    <AppIcon iconName="Check" diameter="14px" color="Colors/Base/White" />
                  ) : (
                    <Box sx={radioDotSx} />
                  ))}
              </Box>
              {label}
            </Box>
          );
        })}
    </Box>
  );
}

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

const answerSx =
  (isSelected: boolean, isDisabled: boolean): SxProps<Theme> =>
  ({ backrs, typography }) => ({
    ...typography['text-sm-medium'],
    display: 'flex',
    alignItems: 'center',
    gap: backrs.spacing['spacing-md'],
    padding: `${backrs.spacing['spacing-md']} ${backrs.spacing['spacing-lg']}`,
    borderRadius: backrs.radius['radius-md'],
    border: `1px solid ${backrs.colors[isSelected ? 'Brand/500' : 'Gray/300']}`,
    backgroundColor: backrs.colors[isSelected ? 'Brand/50' : 'Colors/Base/White'],
    color: backrs.colors['Colors/Text/text-primary'],
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    opacity: isDisabled ? 0.4 : 1,
    textAlign: 'left',
    userSelect: 'none',
    transition: 'all 0.15s ease-in-out',
    ...(!isDisabled && {
      '&:hover': {
        borderColor: backrs.colors['Brand/300'],
        backgroundColor: backrs.colors[isSelected ? 'Brand/50' : 'Brand/25'],
      },
    }),
  });

const indicatorSx =
  (isSelected: boolean, isMulti: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '20px',
    height: '20px',
    borderRadius: isMulti ? backrs.radius['radius-sm'] : '50%',
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    backgroundColor: isSelected ? backrs.colors['Brand/500'] : 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'all 0.15s ease-in-out',
  });

const radioDotSx: SxProps<Theme> = ({ backrs }) => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: backrs.colors['Colors/Base/White'],
});
